import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useResizeObserver } from "@activecollab/components";
import PropTypes from "prop-types";
import { getIsFeatureEnabled } from "~/store/selectors/selector.configOptions";
import { getSortedWorkloadUsersProjectsMap } from "~/store/selectors/selector.workload";
import Angie from "~/utils/angie";

const DEFAULT_NO_ASSIGNMENTS_HEIGHT = 55;

export const DEFAULT_ROW_HEIGHT = 48;
export const CALENDAR_EVENT_ROW_HEIGHT = 48;
export const TASK_ROW_HEIGHT = 80;
export const TRACKED_TIME_HEIGHT = 16;
export const MINUTE_DECIMAL = 0.01667;

const Context = React.createContext({
  scaleStart: 0,
  scaleEnd: 0,
  gridRef: { current: null },
  userHeaderRowIndexes: [],
  expandedUserIds: [],
  rowCount: 0,
  columnCount: 0,
  expanded: true,
  rows: [],
  columns: [],
  cellData: {},
  dimensions: { height: 0, width: 0 },
  trackedTimeOnWorkloadEnabled: false,
  getRowHeight: () => null,
  getColumnWidth: () => null,
  handleCollapse: () => null,
  handleExpand: () => null,
  handleToggleAllRows: () => null,
  getUserIdByRowIndex: () => null,
  getProjectIdByRowIndex: () => null,
  getCellData: () => null,
  getShowCompletedTasks: () => null,
  getIsLastUserProject: () => null,
  getUserHasProjects: () => null,
  getNumberOfCompletedTasks: () => null,
  getTaskById: () => null,
  getEventById: () => null,
  getSubtaskById: () => null,
  getGhostTaskById: () => null,
  getTimeRecordsByTaskId: () => null,
  getWorkloadNumberOfUnassignedTasks: () => null,
});

export const useWorkloadContext = () => React.useContext(Context);

export const WorkloadContext = ({
  userProjectsTasksMap,
  children,
  rows,
  columns,
  colWidth = 80,
  scaleStart,
  scaleEnd,
  tasks = {},
  events = {},
  subtasks = {},
  ghostTasks = {},
  timeRecords = {},
  cellData = {},
  trackedTimeOnWorkloadEnabled = false,
}) => {
  const taskSplitsEnabled = useSelector((state) => getIsFeatureEnabled(state, "workload_plus"));
  const sortedUsersProjectsMap = useSelector(getSortedWorkloadUsersProjectsMap);
  const [expanded, setExpanded] = useState(false);
  const [expandedUserIds, setExpandedUserIds] = useState([]);
  const gridRef = useRef(null);

  const bodyRef = useRef(document.body);
  const windowDimensions = useResizeObserver(bodyRef);

  const countUserRows = useCallback(
    (userId) => {
      let count = 0;
      if (userProjectsTasksMap[userId]) {
        count = Object.keys(userProjectsTasksMap[userId]).length;
      } else {
        count = 1;
      }
      return count;
    },
    [userProjectsTasksMap]
  );

  const [userHeaderRowIndexes, setUserHeaderRowIndexes] = useState(() => {
    const result = [];
    let userId = 0;
    rows.forEach((row, index) => {
      if (index === 0) {
        userId = row.userId;
        result.push(index);
      } else {
        result.push(result[index - 1] + countUserRows(userId) + 1);
        userId = row.userId;
      }
    });
    return result;
  });

  const userIds = useMemo(() => {
    return rows.reduce((prev, next) => {
      prev.push(next.userId);
      return prev;
    }, []);
  }, [rows]);

  const getUserIdByRowIndex = useCallback(
    (rowIndex) => {
      let maxIndex = -1;
      let maxVal = -1;

      for (let i = 0; i < userHeaderRowIndexes.length; i++) {
        if (userHeaderRowIndexes[i] <= rowIndex && userHeaderRowIndexes[i] > maxVal) {
          maxVal = userHeaderRowIndexes[i];
          maxIndex = i;
        }
      }

      if (maxIndex >= 0) {
        return userIds[maxIndex];
      }
      return null;
    },
    [userHeaderRowIndexes, userIds]
  );

  const getProjectIdByRowIndex = useCallback(
    (rowIndex) => {
      const userId = getUserIdByRowIndex(rowIndex);
      const offset = userHeaderRowIndexes[userIds.indexOf(userId)];
      const userProjects = userProjectsTasksMap[userId];
      if (userProjects) {
        const userProjectIds = Object.keys(userProjects);
        const sortedProjectsIds = sortedUsersProjectsMap[userId].filter((projectId) =>
          userProjectIds.includes(`${projectId}`)
        );
        const projectId = sortedProjectsIds[rowIndex - offset - 1];
        if (projectId !== undefined) {
          return projectId;
        }
      }

      return undefined;
    },
    [getUserIdByRowIndex, sortedUsersProjectsMap, userHeaderRowIndexes, userIds, userProjectsTasksMap]
  );

  const columnCount = useMemo(() => {
    return columns?.length + 1;
  }, [columns?.length]);

  const getColumnWidth = useCallback(
    ({ index }) => {
      if (index === 0) {
        if (windowDimensions?.width <= 640) {
          return 70;
        }
        return 270;
      }
      return columns[index - 1].isWorkday ? colWidth : colWidth / 2;
    },
    [colWidth, columns, windowDimensions?.width]
  );

  const getIsLastUserProject = useCallback(
    (rowIndex) => {
      let userId = getUserIdByRowIndex(rowIndex);
      if (userId !== undefined) {
        const userProjects = userProjectsTasksMap[userId];
        if (userProjects) {
          const userProjectIds = Object.keys(userProjects);
          const sortedProjectsIds = sortedUsersProjectsMap[userId].filter((projectId) =>
            userProjectIds.includes(`${projectId}`)
          );
          const offset = userHeaderRowIndexes[userIds.indexOf(userId)];
          if (rowIndex - offset === sortedProjectsIds.length) {
            return true;
          }
        } else {
          return true;
        }
      }
      return false;
    },
    [getUserIdByRowIndex, sortedUsersProjectsMap, userHeaderRowIndexes, userIds, userProjectsTasksMap]
  );

  const getShowCompletedTasks = useCallback(
    (rowIndex) => {
      let userId = getUserIdByRowIndex(rowIndex);

      const userRowData = rows.find((row) => row.userId === userId);

      if (userRowData) {
        if (userRowData.numberOfCompletedTasks > 0) {
          return getIsLastUserProject(rowIndex);
        }
      }
      return false;
    },
    [getIsLastUserProject, getUserIdByRowIndex, rows]
  );

  const getNumberOfCompletedTasks = useCallback(
    (rowIndex) => {
      let userId = getUserIdByRowIndex(rowIndex);

      const userRowData = rows.find((row) => row.userId === userId);

      if (userRowData) {
        return userRowData.numberOfCompletedTasks;
      }
      return 0;
    },
    [getUserIdByRowIndex, rows]
  );

  const getUserHasProjects = useCallback(
    (rowIndex) => {
      let userId = getUserIdByRowIndex(rowIndex);

      const userProjects = userProjectsTasksMap[userId];
      if (userProjects) {
        return true;
      }
      return false;
    },
    [getUserIdByRowIndex, userProjectsTasksMap]
  );

  const getRowHeight = useCallback(
    ({ index }) => {
      let userId = getUserIdByRowIndex(index);
      const offset = userHeaderRowIndexes[userIds.indexOf(userId)];
      if (offset !== index) {
        if (expandedUserIds.includes(userId)) {
          const userProjects = userProjectsTasksMap[userId];
          const projectId = getProjectIdByRowIndex(index);
          if (userProjects && typeof projectId !== "undefined") {
            let additionalHeight = 0;
            if (getShowCompletedTasks(index)) {
              additionalHeight += 30;
            }
            if (getIsLastUserProject(index)) {
              additionalHeight += 8;
            }
            if (projectId === null) {
              return DEFAULT_ROW_HEIGHT + additionalHeight;
            }
            const projectRows = userProjects[projectId];
            if (projectRows) {
              if (taskSplitsEnabled) {
                if (projectId === 0) {
                  return CALENDAR_EVENT_ROW_HEIGHT * projectRows.length + additionalHeight;
                } else {
                  if (trackedTimeOnWorkloadEnabled) {
                    return (TASK_ROW_HEIGHT + TRACKED_TIME_HEIGHT) * projectRows.length + additionalHeight;
                  }
                  return TASK_ROW_HEIGHT * projectRows.length + additionalHeight;
                }
              } else {
                return DEFAULT_ROW_HEIGHT * projectRows.length + additionalHeight;
              }
            }
          }
          return DEFAULT_NO_ASSIGNMENTS_HEIGHT;
        } else {
          return 0;
        }
      }
      return 40;
    },
    [
      expandedUserIds,
      getIsLastUserProject,
      getProjectIdByRowIndex,
      getShowCompletedTasks,
      getUserIdByRowIndex,
      taskSplitsEnabled,
      trackedTimeOnWorkloadEnabled,
      userHeaderRowIndexes,
      userIds,
      userProjectsTasksMap,
    ]
  );

  const rowCount = useMemo(() => {
    let count = userHeaderRowIndexes.length;
    if (userIds.length > 0) {
      userIds.forEach((id) => {
        count += countUserRows(id);
      });
    }

    return count;
  }, [countUserRows, userHeaderRowIndexes, userIds]);

  const handleCollapse = useCallback(
    (rowIndex) => {
      let userId = getUserIdByRowIndex(rowIndex);
      setExpandedUserIds(expandedUserIds.filter((id) => id !== userId));
      // gridRef.current.recomputeGridSize(0, rowIndex);
    },
    [expandedUserIds, getUserIdByRowIndex]
  );

  const handleExpand = useCallback(
    (rowIndex) => {
      let userId = getUserIdByRowIndex(rowIndex);
      Angie.logger.info("WorkloadUserExpanded");
      setExpandedUserIds([...expandedUserIds, userId]);
      // gridRef.current.recomputeGridSize(0, rowIndex);
    },
    [expandedUserIds, getUserIdByRowIndex]
  );

  const handleToggleAllRows = useCallback(() => {
    if (expandedUserIds.length === userHeaderRowIndexes.length) {
      setExpandedUserIds([]);
    } else {
      Angie.logger.info("WorkloadUsersExpanded");
      setExpandedUserIds([...userIds]);
    }
    // gridRef.current.recomputeGridSize(0, 0);
  }, [expandedUserIds.length, userHeaderRowIndexes.length, userIds]);

  const getCellData = useCallback(
    (rowIndex, columnIndex) => {
      let userId = getUserIdByRowIndex(rowIndex);

      const res = [];

      const offset = userHeaderRowIndexes[userIds.indexOf(userId)];
      if (expandedUserIds.includes(userId)) {
        const userProjects = userProjectsTasksMap[userId];
        if (userProjects) {
          const userProjectIds = Object.keys(userProjects);
          const sortedProjectsIds = sortedUsersProjectsMap[userId].filter((projectId) =>
            userProjectIds.includes(`${projectId}`)
          );
          const projectId = sortedProjectsIds[rowIndex - offset - 1];

          const userProject = userProjects[projectId];
          if (userProject) {
            for (const [, value] of Object.entries(userProject)) {
              const entity = value.find((e) => {
                const colIndexes = [];
                for (let i = e.position; i < e.position + e.duration; i++) {
                  colIndexes.push(i);
                }

                return colIndexes.includes(columnIndex);
              });
              if (entity) {
                res.push(entity);
              } else {
                res.push({});
              }
            }
          }
        }
      }

      if (res) {
        return res;
      }
      return null;
    },
    [expandedUserIds, getUserIdByRowIndex, sortedUsersProjectsMap, userHeaderRowIndexes, userIds, userProjectsTasksMap]
  );

  useEffect(() => {
    if (expandedUserIds.length === userHeaderRowIndexes.length) {
      setExpanded(true);
    } else {
      setExpanded(false);
    }
  }, [expandedUserIds.length, userHeaderRowIndexes.length]);

  useEffect(() => {
    if (userProjectsTasksMap) {
      const newUserHeaderRowIndexes = [];
      let userId = 0;
      rows.forEach((row, index) => {
        if (index === 0) {
          userId = row.userId;
          newUserHeaderRowIndexes.push(index);
        } else {
          newUserHeaderRowIndexes.push(newUserHeaderRowIndexes[index - 1] + countUserRows(userId) + 1);
          userId = row.userId;
        }
      });
      setUserHeaderRowIndexes(newUserHeaderRowIndexes);
      gridRef.current?.recomputeGridSize(0, 0);
    }
  }, [countUserRows, expandedUserIds, rows, userHeaderRowIndexes.length, userProjectsTasksMap]);

  const dimensions = useMemo(() => {
    let width = 0;
    for (let i = 0; i < columnCount; i++) {
      width += getColumnWidth({ index: i });
    }

    let height = 0;
    for (let i = 0; i < rowCount; i++) {
      height += getRowHeight({ index: i });
    }

    return {
      width,
      height,
    };
  }, [columnCount, getColumnWidth, getRowHeight, rowCount]);

  const getTaskById = useCallback(
    (id) => {
      return tasks[id];
    },
    [tasks]
  );

  const getEventById = useCallback((id) => events[id], [events]);

  const getSubtaskById = useCallback((id) => subtasks[id], [subtasks]);

  const getGhostTaskById = useCallback((id) => ghostTasks[id], [ghostTasks]);

  const getWorkloadNumberOfUnassignedTasks = useCallback(() => {
    let result = 0;
    if (tasks) {
      result += Object.values(tasks).filter((task) => task.assignee_id === 0).length;
    }
    return result;
  }, [tasks]);

  const getTimeRecordsByTaskId = useCallback((id) => timeRecords[id], [timeRecords]);

  return (
    <Context.Provider
      value={{
        scaleStart,
        scaleEnd,
        gridRef,
        userHeaderRowIndexes,
        rowCount,
        columnCount,
        expandedUserIds,
        expanded,
        rows,
        columns,
        cellData,
        dimensions,
        colWidth,
        trackedTimeOnWorkloadEnabled,
        getRowHeight,
        getColumnWidth,
        handleCollapse,
        handleExpand,
        handleToggleAllRows,
        getUserIdByRowIndex,
        getProjectIdByRowIndex,
        getCellData,
        getShowCompletedTasks,
        getIsLastUserProject,
        getUserHasProjects,
        getNumberOfCompletedTasks,
        getTaskById,
        getEventById,
        getSubtaskById,
        getGhostTaskById,
        getTimeRecordsByTaskId,
        getWorkloadNumberOfUnassignedTasks,
      }}
    >
      {children}
    </Context.Provider>
  );
};

WorkloadContext.propTypes = {
  userProjectsTasksMap: PropTypes.object,
  children: PropTypes.node.isRequired,
  scaleStart: PropTypes.number,
  scaleEnd: PropTypes.number,
  rows: PropTypes.array,
  columns: PropTypes.array,
  colWidth: PropTypes.number,
  tasks: PropTypes.object,
  events: PropTypes.object,
  subtasks: PropTypes.object,
  ghostTasks: PropTypes.object,
  timeRecords: PropTypes.object,
  cellData: PropTypes.object,
  trackedTimeOnWorkloadEnabled: PropTypes.bool,
};
